import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import Papa from 'papaparse';
import { Voter } from '../../types';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

interface VoterCSVImportProps {}

const VoterCSVImport: React.FC<VoterCSVImportProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentCount, setCurrentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [failedVoters, setFailedVoters] = useState<Array<Voter & { errorMessage?: string }>>([]);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [importedFileName, setImportedFileName] = useState<string | null>(null);

  // Retry function with exponential backoff
  const retryAddVoter = async (
    addVoterFunc: ReturnType<typeof httpsCallable>, 
    voter: Voter, 
    maxRetries: number = 3
  ) => {
    let retries = 0;
    while (retries < maxRetries) {
      try {
        return await addVoterFunc(voter);
      } catch (err) {
        retries++;
        if (retries >= maxRetries) {
          throw err; // Throw if max retries reached
        }
        
        // Exponential backoff with jitter
        const baseDelay = 1000; // 1 second
        const delay = baseDelay * Math.pow(2, retries) + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const handleDataImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);
    setCurrentCount(0);
    setFailedVoters([]);
    setSelectedFileName(file.name);
    setImportedFileName(null);

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        try {
          const voters: Voter[] = results.data.map((row: any) => ({
            id: '', // Will be generated by Firestore
            name: row.name,
            precinct: row.precinct_number, // Changed from precinct_number to precinct
            barangay: row.barangay,
            city_municipality: row.city_municipality,
            district: row.district,
            province: row.province,
            region: row.region,
            organizations: [], // Empty array during import
          }));

          setTotalCount(voters.length);

          const addVoterFunc = httpsCallable(functions, 'add_voter');
          const failedVoters: Array<Voter & { errorMessage?: string }> = [];

          for (const voter of voters) {
            try {
              await retryAddVoter(addVoterFunc, voter);
              setCurrentCount(prevCount => prevCount + 1);
            } catch (err) {
              console.error(`Failed to import voter: ${voter.name}`, err);
              failedVoters.push({
                ...voter, 
                errorMessage: err instanceof Error ? err.message : 'Unknown error occurred'
              });
            }
          }

          setIsLoading(false);
          setSelectedFileName(null);
          setImportedFileName(file.name);

          if (failedVoters.length > 0) {
            setError(`Failed to import ${failedVoters.length} voters from ${file.name}.`);
            setFailedVoters(failedVoters);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An unknown error occurred');
          setIsLoading(false);
          setSelectedFileName(null);
        }
      },
      error: (error) => {
        setError(error.message);
        setIsLoading(false);
        setSelectedFileName(null);
      }
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Voters CSV Import
        </Typography>
      </Box>
      
      <Box display="flex" alignItems="center" gap={2}>
        <input 
          id="voter-csv-upload"
          type="file" 
          accept=".csv" 
          onChange={handleDataImport} 
          disabled={isLoading}
          aria-label="Upload voter CSV file"
          title="Upload CSV file with voter information"
          style={{ display: 'none' }}
        />
        <Button 
          variant="contained" 
          component="label" 
          htmlFor="voter-csv-upload"
          disabled={isLoading}
        >
          Choose File
        </Button>
        {selectedFileName && (
          <Typography variant="body2" color="textSecondary">
            Selected file: {selectedFileName}
          </Typography>
        )}
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }} 
          action={
            <Button color="inherit" size="small" onClick={() => {
              setError(null);
              setFailedVoters([]);
              setSelectedFileName(null);
              setImportedFileName(null);
            }}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {(totalCount > 0 || currentCount > 0) && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {isLoading 
            ? `Importing voters: ${currentCount} / ${totalCount}` 
            : `Successfully imported ${currentCount} voters from ${importedFileName}. Please rebuild indexes once the import queue in Google Cloud Tasks is empty.`}
        </Alert>
      )}

      {failedVoters.length > 0 && (
        <Box mt={2}>
          <Typography variant="h6" gutterBottom>
            Failed Voter Imports
          </Typography>
          <DataGrid
            rows={failedVoters.map((voter, index) => ({
              id: index,
              name: voter.name,
              precinct: voter.precinct,
              barangay: voter.barangay,
              errorMessage: voter.errorMessage || 'Unknown error'
            }))}
            columns={[
              { field: 'name', headerName: 'Name', flex: 1 },
              { field: 'precinct', headerName: 'Precinct', width: 150 },
              { field: 'barangay', headerName: 'Barangay', width: 150 },
              { field: 'errorMessage', headerName: 'Error', flex: 1 }
            ]}
            autoHeight
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 }
              }
            }}
            pageSizeOptions={[5, 10, 25]}
          />
        </Box>
      )}
    </Box>
  );
};

export default VoterCSVImport;
